/**
 * Order-details monetary display SSOT — ledger ILS is never passed through convertPrice / localStorage currentRate.
 */

import {
  formatAgentWalletAmountIls,
  readCatalogDisplayFxSnapshotForTrace,
} from "../../services/currencyService";
import {
  formatDualCurrencyDisplay,
  formatIlsIntegerDisplay,
  resolveUsdCeilFromIls,
} from "../formatDualCurrencyDisplay";

function finiteIls(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.ceil(n - 1e-9));
}

function finiteUsd(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.ceil(n - 1e-9);
}

function extractLockedUsdRate(currencyRate) {
  if (currencyRate != null && Number.isFinite(Number(currencyRate)) && Number(currencyRate) > 0) {
    return Number(currencyRate);
  }
  const n = Number(currencyRate?.USD);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function lockedRateMap(currencyRate) {
  const rate = extractLockedUsdRate(currencyRate);
  if (rate != null) return { USD: rate };
  return currencyRate && typeof currencyRate === "object" ? currencyRate : null;
}

/**
 * True when stored USD is usable as the companion for this ILS amount.
 * Keeps locked booking USD even when FX rounding drifts.
 * Rejects mixed pairs like ₪92 + $60 (2× $30 floor) at ~3.06 NIS/$.
 */
export function authoritativeUsdMatchesIlsLedger(ilsAmount, usdAmount, currencyRate) {
  const ils = finiteIls(ilsAmount);
  const usd = finiteUsd(usdAmount);
  const rate = extractLockedUsdRate(currencyRate);
  if (ils == null || usd == null) return usd != null;
  if (rate == null) return true;
  const implied = ils / rate;
  if (!(implied > 0)) return false;
  if (Math.abs(usd - implied) <= 1.01) return true;
  const ratio = usd / implied;
  // 2× (or ½×) direction-floor mixed with ILS remainder — not the same amount.
  if (ratio >= 1.6 && ratio <= 2.4) return false;
  if (ratio >= 0.4 && ratio <= 0.65) return false;
  return true;
}

/**
 * Pair ILS ledger with one USD companion. Never mix remainder-ILS with a USD floor.
 * Unpaid balance uses peer total USD when ILS matches the order total ($167 vs $168).
 */
export function resolvePairedUsdForOrderDetails({
  ilsAmount,
  authoritativeUsd = null,
  currencyRate = null,
  peerIls = null,
  peerUsd = null,
} = {}) {
  const ils = finiteIls(ilsAmount);
  if (ils == null || ils <= 0) return null;

  const peerIlsRounded = finiteIls(peerIls);
  const peerUsdRounded = finiteUsd(peerUsd);
  if (
    peerIlsRounded != null &&
    peerUsdRounded != null &&
    Math.abs(ils - peerIlsRounded) < 1.01
  ) {
    return peerUsdRounded;
  }

  const rate =
    extractLockedUsdRate(currencyRate) ??
    (peerIlsRounded != null && peerUsdRounded != null && peerUsdRounded > 0
      ? peerIlsRounded / peerUsdRounded
      : null);
  const rateMap = rate != null ? { USD: rate } : lockedRateMap(currencyRate);
  const auth = finiteUsd(authoritativeUsd);
  const rateForMatch = rate != null ? rate : currencyRate;
  if (auth != null && authoritativeUsdMatchesIlsLedger(ils, auth, rateForMatch)) {
    return auth;
  }
  if (auth != null && rate != null) {
    const derived = Math.round(ils / rate);
    if (derived > 0) return derived;
  }
  return resolveUsdCeilFromIls(ils, undefined, rateMap);
}

/**
 * @param {Record<string, unknown>} payload
 */
export function traceOrderDetailsTotalRender(payload) {
  const catalogFx = readCatalogDisplayFxSnapshotForTrace();
  console.log("[ORDER_DETAILS_TOTAL_RENDER_TRACE]", {
    ...payload,
    catalog_localStorage_rate: catalogFx.rate,
    catalog_localStorage_currency: catalogFx.currencyInfoText,
  });
}

/**
 * ₪ ledger display only (no FX multiply).
 * @param {number|null|undefined} ilsAmount
 * @returns {string}
 */
export function formatOrderDetailsIlsLedger(ilsAmount) {
  const ils = finiteIls(ilsAmount);
  if (ils == null) return "לא זמין";
  return formatIlsIntegerDisplay(ils) || formatAgentWalletAmountIls(ils);
}

/**
 * ILS primary + optional USD secondary from SSOT (order ourprice / API), not catalog currentRate.
 * Prefer authoritativeUsd locked at booking. currencyRate should be the order's locked fx_rate
 * ({ USD }) — never the live admin rate — when used as fallback reverse-FX.
 */
export function formatOrderDetailsIlsWithUsdSecondary({
  ilsAmount,
  authoritativeUsd = null,
  currencyRate = null,
  peerIls = null,
  peerUsd = null,
}) {
  const ilsRounded = finiteIls(ilsAmount);
  if (ilsRounded == null) return "לא זמין";

  const usdResolved = resolvePairedUsdForOrderDetails({
    ilsAmount: ilsRounded,
    authoritativeUsd,
    currencyRate,
    peerIls,
    peerUsd,
  });

  const dual = formatDualCurrencyDisplay({
    ils: ilsRounded,
    usd: usdResolved,
    primaryCurrency: "ILS",
  });

  if (!dual) {
    return formatOrderDetailsIlsLedger(ilsRounded);
  }

  return dual.secondaryFormatted
    ? `${dual.primaryFormatted} ${dual.secondaryFormatted}`
    : dual.primaryFormatted;
}

/**
 * Build summary hero strings + trace for order-details surfaces.
 */
export function buildOrderDetailsTotalSummaryDisplay({
  orderId,
  componentName,
  ilsAmount,
  authoritativeUsd = null,
  currencyRate = null,
  sourcePath,
}) {
  const rawAmount = Number(ilsAmount);
  const ilsRounded = finiteIls(ilsAmount);
  const display = formatOrderDetailsIlsWithUsdSecondary({
    ilsAmount: ilsRounded,
    authoritativeUsd,
    currencyRate,
  });

  const catalogFx = readCatalogDisplayFxSnapshotForTrace();
  const catalogRate = catalogFx.rate ? parseFloat(String(catalogFx.rate)) : NaN;
  const wouldConvertWithCatalog =
    ilsRounded != null &&
    Number.isFinite(catalogRate) &&
    catalogRate > 0 &&
    Math.abs(ilsRounded * catalogRate - ilsRounded) > 0.01;

  traceOrderDetailsTotalRender({
    order_id: orderId ?? null,
    component_name: componentName,
    raw_amount: Number.isFinite(rawAmount) ? rawAmount : null,
    raw_currency: "ILS",
    selected_display_amount: display,
    selected_display_currency: "ILS",
    did_convert: false,
    rate_used:
      finiteUsd(authoritativeUsd) != null
        ? { mode: "authoritative_usd", usd: finiteUsd(authoritativeUsd) }
        : currencyRate?.USD != null
          ? { mode: "context_usd_rate", usd_per_ils: currencyRate.USD }
          : null,
    source_path: sourcePath,
    catalog_would_have_multiplied: wouldConvertWithCatalog,
    catalog_hypothetical_wrong_display:
      wouldConvertWithCatalog && ilsRounded != null
        ? `${Math.round(ilsRounded * catalogRate)}₪`
        : null,
  });

  return {
    priceMain: display,
    priceSub: null,
    ilsRounded,
  };
}
